import { useEffect } from "react";
import Image from "next/image";

import { ReturnPopup } from "./ReturnPopup";
import { DiscountsPopup } from "./DiscountsPopup";
import { TicketsPopup } from "./TicketsPopup";

import { GlobalWidgetListPopup } from "./GlobalWidgetListPopup";

import cross from "../../../../public/cross.svg";

import { Receipt, ExtendedViewItem } from "@/types/receipt";
import {
  PopupState,
  CLOSED_POPUP_STATE,
  isGlobalWidgetType,
  QUICK_SEND_ACCOUNTING_POPUP_TYPE,
} from "@/constants/popup";
import { ServiceViewPopup } from "./ServiceViewPopup";
import { ExtendedReceiptViewPopup } from "./ExtendedReceiptViewPopup";
import { QuickSendAccountingPopup } from "./QuickSendAccountingPopup";

const EMPTY_EXTENDED_VIEW_ITEMS: ExtendedViewItem[] = [];
const EMPTY_EXTENDED_VIEW_IMAGES: Record<string, string> = {};

interface PopupWrapperProps {
  receipt: Receipt;
  lang: string;
  popupOpen: PopupState;
  showPopup: (popup: PopupState) => void;
  mainReceiptBarcodeImage: string;
  discountBarcodeImages: string[];
  ticketBarcodeImages: { [articleIndex: string]: string[] };
  extendedViewItems?: ExtendedViewItem[];
  extendedViewImages?: { [key: string]: string };
  user_id?: string;
  receipt_id?: string;
  email?: string | null;
  phoneNumber?: string | null;
}

export const PopupWrapper = ({
  receipt,
  lang,
  popupOpen,
  showPopup,
  mainReceiptBarcodeImage,
  discountBarcodeImages,
  ticketBarcodeImages,
  extendedViewItems,
  extendedViewImages,
  user_id,
  receipt_id,
  email,
  phoneNumber,
}: PopupWrapperProps) => {
  // Add scroll lock effect when popup is mounted
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const closeButton = (
    <button
      type="button"
      onClick={() => showPopup(CLOSED_POPUP_STATE)}
      className="popup-close-button"
      aria-label="Close popup"
    >
      <Image src={cross} alt="Collapse" />
    </button>
  );

  return (
    <div className="popup">
      <div className="popup-wrapper">
        <div className="popup-body">
          {/* Return / bar code */}
          {popupOpen.type === "return" && (
            <ReturnPopup
              receipt={receipt}
              lang={lang}
              mainReceiptBarcodeImage={mainReceiptBarcodeImage}
              extendedViewItems={extendedViewItems ?? EMPTY_EXTENDED_VIEW_ITEMS}
              extendedViewImages={extendedViewImages ?? EMPTY_EXTENDED_VIEW_IMAGES}
              user_id={user_id}
              receipt_id={receipt_id}
              email={email}
              phoneNumber={phoneNumber}
              closeButton={closeButton}
            />
          )}
          {/* Discounts */}
          {popupOpen.type === "discounts" && (
            <DiscountsPopup
              receipt={receipt}
              lang={lang}
              discountBarcodeImages={discountBarcodeImages}
              closeButton={closeButton}
            />
          )}
          {/* Extended View Items */}
          {popupOpen.type === "extended_view" &&
            extendedViewItems &&
            extendedViewImages && (
              <ServiceViewPopup
                extendedViewItems={extendedViewItems}
                extendedViewImages={extendedViewImages}
                lang={lang}
                user_id={user_id}
                receipt_id={receipt_id}
                receipt={receipt}
                email={email}
                phoneNumber={phoneNumber}
                closeButton={closeButton}
              />
            )}
          {/* Individual Widget Popups */}
          {popupOpen.type.startsWith("widget_") &&
            extendedViewItems &&
            extendedViewImages && (
              <ExtendedReceiptViewPopup
                extendedViewItem={extendedViewItems[popupOpen.index]}
                extendedViewImage={
                  extendedViewImages[
                    `${
                      extendedViewItems[popupOpen.index]?.extended_view_position
                    }_${popupOpen.index}`
                  ]
                }
                lang={lang}
                user_id={user_id}
                receipt_id={receipt_id}
                receipt={receipt}
                email={email}
                phoneNumber={phoneNumber}
                article={popupOpen.article}
                closeButton={closeButton}
              />
            )}
          {/* Quick-send accounting modal */}
          {popupOpen.type === QUICK_SEND_ACCOUNTING_POPUP_TYPE &&
            extendedViewItems &&
            extendedViewImages &&
            user_id &&
            receipt_id && (
              <QuickSendAccountingPopup
                extendedViewItems={extendedViewItems}
                extendedViewImages={extendedViewImages}
                lang={lang}
                receipt={receipt}
                userId={user_id}
                receiptId={receipt_id}
                showPopup={showPopup}
                closeButton={closeButton}
              />
            )}
          {/* Global Widget List Popups */}
          {isGlobalWidgetType(popupOpen.type) &&
            extendedViewItems &&
            extendedViewImages && (
              <GlobalWidgetListPopup
                widgetType={popupOpen.type}
                extendedViewItems={extendedViewItems}
                extendedViewImages={extendedViewImages}
                lang={lang}
                receipt={receipt}
                user_id={user_id}
                receipt_id={receipt_id}
                email={email}
                phoneNumber={phoneNumber}
                closeButton={closeButton}
              />
            )}
          {/* Tickets */}
          {receipt.articles?.map((article, index) => {
            if (
              popupOpen.type === article.type &&
              index === popupOpen.index &&
              article.barcodes?.length &&
              article.barcodes?.length > 0
            ) {
              return (
                <TicketsPopup
                  key={`${article.art_number}-${index}`}
                  receipt={receipt}
                  lang={lang}
                  ticketBarcodeImages={ticketBarcodeImages}
                  article={article}
                  index={index}
                  closeButton={closeButton}
                />
              );
            }
            return null;
          })}
        </div>
      </div>

      <button
        type="button"
        aria-label="Close popup"
        className="popup-bg"
        onClick={() => showPopup(CLOSED_POPUP_STATE)}
      />
    </div>
  );
};
